/**
 * Diagnostic Collector Service
 *
 * Story 15.12.1: Diagnostic Capture + AI Analysis
 * Epic 15.12: AI-Powered Feedback & Diagnostic System
 *
 * Сбор диагностических данных: console errors/warns, window.onerror,
 * unhandledrejection. Circular buffer (max 20). Fire-and-forget pattern.
 *
 * НЕ модифицирует ErrorCaptureService — работает ОТДЕЛЬНО.
 */
import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';

// Типы диагностических данных (должны совпадать с backend Pydantic моделями)
export interface DiagnosticConsoleEntry {
  timestamp: string;
  level: 'error' | 'warn';
  message: string;
  stack?: string;
}

export interface DiagnosticNetworkEntry {
  timestamp: string;
  url: string;
  method: string;
  status: number;
  duration_ms: number;
  response_preview: string;
}

export interface DiagnosticBrowser {
  url: string;
  route: string;
  query_params: Record<string, string>;
  viewport: string;
  dpr: number;
  user_agent: string;
  time_on_page_sec: number;
  angular_version: string | null;
}

export interface DiagnosticData {
  version: string;
  console_errors: DiagnosticConsoleEntry[];
  failed_requests: DiagnosticNetworkEntry[];
  browser: DiagnosticBrowser | null;
}

// Circular buffer — фиксированный размер, FIFO
class CircularBuffer<T> {
  private buffer: T[] = [];
  private readonly maxSize: number;

  constructor(maxSize: number) {
    this.maxSize = maxSize;
  }

  push(item: T): void {
    if (this.buffer.length >= this.maxSize) {
      this.buffer.shift();
    }
    this.buffer.push(item);
  }

  getAll(): T[] {
    return [...this.buffer];
  }

  clear(): void {
    this.buffer = [];
  }

  get length(): number {
    return this.buffer.length;
  }
}

@Injectable({ providedIn: 'root' })
export class DiagnosticCollectorService implements OnDestroy {
  private consoleBuffer = new CircularBuffer<DiagnosticConsoleEntry>(20);
  private networkBuffer = new CircularBuffer<DiagnosticNetworkEntry>(20);
  private initialized = false;
  private pageLoadTime = Date.now();

  // Сохраняем оригинальные console методы
  private originalConsoleError: typeof console.error = console.error;
  private originalConsoleWarn: typeof console.warn = console.warn;

  // Recursion guard
  private isCapturing = false;

  // Event handler references (для cleanup)
  private windowErrorHandler: ((event: ErrorEvent) => void) | null = null;
  private unhandledRejectionHandler: ((event: PromiseRejectionEvent) => void) | null = null;

  constructor(private router: Router) {}

  /**
   * Инициализация сбора диагностики.
   * Вызывается один раз при старте приложения (если feature flag ON).
   */
  initialize(): void {
    if (this.initialized) return;
    if (!this.isEnabled()) return;

    try {
      this.patchConsole();
      this.attachWindowHandlers();
      this.initialized = true;
    } catch (e) {
      // Fire-and-forget: не ломаем приложение
      this.originalConsoleWarn('[DiagnosticCollector] Initialization failed:', e);
    }
  }

  /**
   * Проверяет feature flag.
   */
  private isEnabled(): boolean {
    return (environment as any).diagnosticCaptureEnabled !== false;
  }

  /**
   * Monkey-patch console.error и console.warn.
   */
  private patchConsole(): void {
    const self = this;

    console.error = function (...args: any[]) {
      self.captureConsoleEntry('error', args);
      self.originalConsoleError.apply(console, args);
    };

    console.warn = function (...args: any[]) {
      self.captureConsoleEntry('warn', args);
      self.originalConsoleWarn.apply(console, args);
    };
  }

  /**
   * Захват записи console.error/warn в буфер.
   */
  private captureConsoleEntry(level: 'error' | 'warn', args: any[]): void {
    // Recursion guard — console.warn внутри коллектора не рекурсирует
    if (this.isCapturing) return;
    this.isCapturing = true;

    try {
      const message = args
        .map(arg => {
          if (arg instanceof Error) return arg.message;
          if (typeof arg === 'string') return arg;
          try { return JSON.stringify(arg); } catch { return String(arg); }
        })
        .join(' ')
        .slice(0, 2048);

      let stack: string | undefined;
      for (const arg of args) {
        if (arg instanceof Error && arg.stack) {
          stack = arg.stack.slice(0, 4096);
          break;
        }
      }

      this.consoleBuffer.push({
        timestamp: new Date().toISOString(),
        level,
        message,
        stack,
      });
    } catch {
      // Fire-and-forget
    } finally {
      this.isCapturing = false;
    }
  }

  /**
   * Attach window.onerror и unhandledrejection → тот же consoleBuffer.
   */
  private attachWindowHandlers(): void {
    this.windowErrorHandler = (event: ErrorEvent) => {
      try {
        this.consoleBuffer.push({
          timestamp: new Date().toISOString(),
          level: 'error',
          message: (event.message || 'Unknown error').slice(0, 2048),
          stack: event.error?.stack?.slice(0, 4096),
        });
      } catch {
        // Fire-and-forget
      }
    };

    this.unhandledRejectionHandler = (event: PromiseRejectionEvent) => {
      try {
        const reason = event.reason;
        const message = reason instanceof Error
          ? reason.message
          : (typeof reason === 'string' ? reason : 'Unhandled promise rejection');

        this.consoleBuffer.push({
          timestamp: new Date().toISOString(),
          level: 'error',
          message: message.slice(0, 2048),
          stack: reason instanceof Error ? reason.stack?.slice(0, 4096) : undefined,
        });
      } catch {
        // Fire-and-forget
      }
    };

    window.addEventListener('error', this.windowErrorHandler);
    window.addEventListener('unhandledrejection', this.unhandledRejectionHandler);
  }

  /**
   * Добавить запись о неудачном HTTP запросе (вызывается из DiagnosticNetworkInterceptor).
   */
  addNetworkEntry(entry: DiagnosticNetworkEntry): void {
    try {
      this.networkBuffer.push(entry);
    } catch {
      // Fire-and-forget
    }
  }

  /**
   * Собрать snapshot диагностических данных.
   * Вызывается при submit feedback.
   */
  getSnapshot(): DiagnosticData {
    return {
      version: '1.0',
      console_errors: this.consoleBuffer.getAll(),
      failed_requests: this.networkBuffer.getAll(),
      browser: this.collectBrowserContext(),
    };
  }

  /**
   * Собрать контекст браузера.
   */
  private collectBrowserContext(): DiagnosticBrowser {
    const queryParams: Record<string, string> = {};
    try {
      const params = new URLSearchParams(window.location.search);
      params.forEach((value, key) => { queryParams[key] = value; });
    } catch {
      // ignore
    }

    return {
      url: window.location.href,
      route: this.router.url || window.location.pathname,
      query_params: queryParams,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      dpr: window.devicePixelRatio || 1,
      user_agent: navigator.userAgent,
      time_on_page_sec: Math.floor((Date.now() - this.pageLoadTime) / 1000),
      angular_version: this.getAngularVersion(),
    };
  }

  /**
   * Получить версию Angular (если доступна).
   */
  private getAngularVersion(): string | null {
    try {
      const versionEl = document.querySelector('[ng-version]');
      return versionEl?.getAttribute('ng-version') || null;
    } catch {
      return null;
    }
  }

  ngOnDestroy(): void {
    // Восстанавливаем console
    if (this.initialized) {
      console.error = this.originalConsoleError;
      console.warn = this.originalConsoleWarn;
    }

    // Убираем window handlers
    if (this.windowErrorHandler) {
      window.removeEventListener('error', this.windowErrorHandler);
    }
    if (this.unhandledRejectionHandler) {
      window.removeEventListener('unhandledrejection', this.unhandledRejectionHandler);
    }
  }
}
